/**
 * Video Service
 * Manages video generation jobs in Firestore
 * Cloud video generation with FFmpeg on Cloud Run
 */

import {
  collection,
  doc,
  addDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  getDocs,
  getDoc,
  Timestamp,
  serverTimestamp,
  limit,
} from "firebase/firestore";
import { db } from "@/config/firebase";
import type { VideoJob } from "@/types";

interface RenderingLimitInfo {
  used: number;
  limit: number;
  isUnlimited: boolean;
  canRender: boolean;
  tier: string;
}

const VIDEO_JOBS_COLLECTION = "videoJobs";

/**
 * Create a new video job
 */
export async function createVideoJob(
  userId: string,
  projectId: string,
  projectName: string,
  viewId: string,
  viewName: string,
  config: {
    resolution: "1080p" | "4k";
    fps: 15 | 24 | 25 | 30 | 60;
    aspectRatio?: "16:9" | "9:16" | "4:3" | "3:4" | "1:1";
    photoIds: string[];
    photoCount: number;
    dateRange: { start: string; end: string };
    adminCreated?: boolean;
    createdBy?: string;
  }
): Promise<string> {
  const jobData: Record<string, unknown> = {
    userId,
    projectId,
    projectName,
    viewId,
    viewName,
    status: "pending",
    resolution: config.resolution,
    fps: config.fps,
    aspectRatio: config.aspectRatio || "16:9",
    photoIds: config.photoIds,
    photoCount: config.photoCount,
    dateRange: config.dateRange,
    progress: 0,
    createdAt: serverTimestamp(),
  };

  if (config.adminCreated) {
    jobData.adminCreated = true;
    jobData.createdBy = config.createdBy;
  }

  const docRef = await addDoc(collection(db, VIDEO_JOBS_COLLECTION), jobData);
  return docRef.id;
}

/**
 * Check if user has an active (pending/processing) video job
 */
export async function hasActiveVideoJob(userId: string): Promise<boolean> {
  const q = query(
    collection(db, VIDEO_JOBS_COLLECTION),
    where("userId", "==", userId),
    where("status", "in", ["pending", "downloading", "encoding", "uploading"]),
    limit(1)
  );

  const snapshot = await getDocs(q);
  return !snapshot.empty;
}

/**
 * Subscribe to user's video jobs
 */
export function subscribeToVideoJobs(
  userId: string,
  callback: (jobs: VideoJob[]) => void
): () => void {
  const q = query(
    collection(db, VIDEO_JOBS_COLLECTION),
    where("userId", "==", userId),
    orderBy("createdAt", "desc")
  );

  return onSnapshot(q, (snapshot) => {
    const jobs = snapshot.docs.map((doc) => ({
      ...doc.data(),
      jobId: doc.id,
    })) as VideoJob[];
    callback(jobs);
  });
}

/**
 * Subscribe to a specific video job
 */
export function subscribeToVideoJob(
  jobId: string,
  callback: (job: VideoJob | null) => void
): () => void {
  const docRef = doc(db, VIDEO_JOBS_COLLECTION, jobId);

  return onSnapshot(docRef, (snapshot) => {
    if (snapshot.exists()) {
      callback({
        ...snapshot.data(),
        jobId: snapshot.id,
      } as VideoJob);
    } else {
      callback(null);
    }
  });
}

/**
 * Delete a video job
 */
export async function deleteVideoJob(jobId: string): Promise<void> {
  const docRef = doc(db, VIDEO_JOBS_COLLECTION, jobId);
  await deleteDoc(docRef);
}

/**
 * Get status badge color for video job status
 */
export function getVideoStatusColor(status: VideoJob["status"]): string {
  switch (status) {
    case "pending":
      return "default";
    case "downloading":
    case "encoding":
    case "uploading":
      return "processing";
    case "completed":
      return "success";
    case "failed":
      return "error";
    default:
      return "default";
  }
}

/**
 * Get status label for video job status
 */
export function getVideoStatusLabel(status: VideoJob["status"]): string {
  switch (status) {
    case "pending":
      return "Queued";
    case "downloading":
      return "Downloading photos";
    case "encoding":
      return "Encoding video";
    case "uploading":
      return "Uploading";
    case "completed":
      return "Ready";
    case "failed":
      return "Failed";
    default:
      return status;
  }
}

/**
 * Format video duration for display
 */
export function formatVideoDuration(seconds: number): string {
  if (seconds < 1) return "< 1s";
  if (seconds < 60) return `${Math.round(seconds)}s`;
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = Math.round(seconds % 60);
  if (remainingSeconds === 0) return `${minutes}m`;
  return `${minutes}m ${remainingSeconds}s`;
}

/**
 * Timespan in seconds (share feature)
 */
export const TIMESPAN_SECONDS: Record<string, number> = {
  "1h": 3600,
  "1d": 86400,
  "3d": 259200,
  "1w": 604800,
  "2w": 1209600,
  "1m": 2592000,
};

/**
 * Estimate video duration for a timespan based on capture interval and fps.
 * Returns seconds. Returns null if interval is unknown.
 */
export function estimateVideoDuration(
  timespan: string,
  captureIntervalSec: number | undefined,
  fps: number
): number | null {
  const timespanSec = TIMESPAN_SECONDS[timespan];
  if (!timespanSec || !captureIntervalSec || captureIntervalSec <= 0 || fps <= 0) {
    return null;
  }
  const estimatedPhotos = timespanSec / captureIntervalSec;
  return estimatedPhotos / fps;
}

/**
 * Format file size for display
 */
export function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

/**
 * Calculate time remaining until expiration
 */
export function getTimeUntilExpiration(expiresAt: Timestamp): string {
  const now = new Date();
  const expires = expiresAt.toDate();
  const diffMs = expires.getTime() - now.getTime();

  if (diffMs <= 0) {
    return "Expired";
  }

  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));

  if (days > 0) {
    return `${days}d ${hours}h left`;
  }
  if (hours > 0) {
    return `${hours}h left`;
  }

  const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
  return `${minutes}m left`;
}

/**
 * Get active jobs count (for badge display)
 */
export function getActiveJobsCount(jobs: VideoJob[]): number {
  return jobs.filter((job) =>
    ["pending", "downloading", "encoding", "uploading"].includes(job.status)
  ).length;
}

/**
 * Get completed jobs that are not expired
 */
export function getAvailableVideos(jobs: VideoJob[]): VideoJob[] {
  const now = new Date();
  return jobs.filter((job) => {
    if (job.status !== "completed") return false;
    if (!job.expiresAt) return false;
    return job.expiresAt.toDate() > now;
  });
}

/**
 * Get rendering limit info for a view
 * Counts completed renderings in the current month
 */
export async function getRenderingLimitInfo(
  viewId: string,
  viewTier: string = "free"
): Promise<RenderingLimitInfo> {
  // Get plan config
  const plansDoc = await getDoc(doc(db, "config", "plans"));
  const plansConfig = plansDoc.data();
  const planConfig = plansConfig?.plans?.[viewTier];
  const renderingsPerMonth = planConfig?.limits?.renderingsPerMonth ?? 3;
  const isUnlimited = renderingsPerMonth === -1;

  if (isUnlimited) {
    return {
      used: 0,
      limit: -1,
      isUnlimited: true,
      canRender: true,
      tier: viewTier,
    };
  }

  // Count completed renderings this month for this view
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  const q = query(
    collection(db, VIDEO_JOBS_COLLECTION),
    where("viewId", "==", viewId),
    where("status", "==", "completed"),
    where("createdAt", ">=", Timestamp.fromDate(startOfMonth))
  );

  const snapshot = await getDocs(q);
  const used = snapshot.size;

  return {
    used,
    limit: renderingsPerMonth,
    isUnlimited: false,
    canRender: used < renderingsPerMonth,
    tier: viewTier,
  };
}

/**
 * Check if a view can create a new rendering
 * Returns error message if limit reached, null if OK
 */
export async function checkRenderingLimit(
  viewId: string,
  viewTier: string = "free"
): Promise<string | null> {
  const info = await getRenderingLimitInfo(viewId, viewTier);

  if (info.canRender) {
    return null;
  }

  return `You've used all ${info.limit} video renderings for this month. Upgrade your plan to generate more videos.`;
}
